import {
    Button,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Modal,
    Segmented,
    Select,
    Space,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography
} from "antd";
import {CheckOutlined, ClearOutlined, CloseOutlined, CopyOutlined, EditOutlined, SearchOutlined, SettingOutlined} from "@ant-design/icons";
import {CSSProperties, useEffect, useState} from "react";
import dayjs from "dayjs";
import CommonTable from "@/component/table/common-table";
import {InfoLine, Label} from "@/component/table";
import Cell from "@/component/table/cell";
import {PageList, RequestPageInfo} from "@/repo/model/common";
import {SecondSaleCategoryStat, SecondSaleYearCard} from "@/repo/model/second-sale-card";
import {useUserSessionContext} from "@/repo/context";
import Repository from "@/repo/repository";

const PRODUCT_CATEGORY_TAG = 6;
const ADVISER_GROUP_TAG = 5;
const ALL_GROUP_VALUE = 'all';
const SECOND_SALE_VALUE = 'secondSale';
const YEAR_CARD_COLUMN_WIDTH = 250;
const CUSTOMER_COLUMN_WIDTH = 120;
const CARD_COUNT_COLUMN_WIDTH = 138;
const ADVISER_COLUMN_WIDTH = 166;
const BOX_COUNT_COLUMN_WIDTH = 86;
const DELIVERY_PLAN_COLUMN_WIDTH = 86;
const LAST_DELIVERY_TIME_COLUMN_WIDTH = 126;
const BOOLEAN_COLUMN_WIDTH = 82;
const EXPANDED_CONTENT_COLUMN_WIDTH = 170;
const MAINTENANCE_COLUMN_WIDTH = 150;
const BIRTHDAY_COLUMN_WIDTH = 88;
const BIRTHDAY_GIFT_COLUMN_WIDTH = 150;
const B_SAMPLE_COLUMN_WIDTH = 88;
const REMARK_COLUMN_WIDTH = 180;
const filterPanelStyle: CSSProperties = {display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end'};
const filterRowStyle: CSSProperties = {justifyContent: 'flex-end', maxWidth: 1120};
const idSearchInputStyle: CSSProperties = {width: 96};
const searchInputStyle: CSSProperties = {width: 132};
const adviserSearchInputStyle: CSSProperties = {width: 148};
const selectInputStyle: CSSProperties = {width: 110};
const remainingBoxSelectStyle: CSSProperties = {width: 150};
const lastDeliverySelectStyle: CSSProperties = {width: 160};
const birthdayRangeInputStyle: CSSProperties = {width: 230};
const yearCardColumnStyle: CSSProperties = {width: 230};
const personColumnStyle: CSSProperties = {width: 104};
const adviserColumnStyle: CSSProperties = {width: 164};
const CUSTOMER_LEVEL_RULE = '分级规则：A=订单金额7000元以上；B=订单金额5000-7000元；C=订单金额3000-5000元；D=订单金额3000元以下';
const SECOND_SALE_CATEGORY_STORAGE_PREFIX = 'secondSaleStatCategoryIds';
const CUSTOMER_LEVEL_OPTIONS = [
    {label: 'A级', value: 'A'},
    {label: 'B级', value: 'B'},
    {label: 'C级', value: 'C'},
    {label: 'D级', value: 'D'},
];
const REMAINING_BOX_RANGE_OPTIONS = [
    {label: '空卡', value: 'EMPTY'},
    {label: '10盒以内', value: 'LE_10'},
    {label: '11-20盒', value: 'RANGE_11_20'},
    {label: '20盒以上', value: 'GT_20'},
];
const LAST_DELIVERY_RANGE_OPTIONS = [
    {label: '90天以内', value: 'WITHIN_90'},
    {label: '91-180天', value: 'RANGE_91_180'},
    {label: '181-365天', value: 'RANGE_181_365'},
    {label: '365天以上', value: 'GT_365'},
];
type SecondSaleSearch = {
    yearCardId?: number;
    yearCardOrderNo?: string;
    customerPhone?: string;
    firstSaleAdviserPhone?: string;
    secondSaleAdviserPhone?: string;
    hasDeliveryPlan?: boolean;
    customerLevel?: string;
    remainingBoxRange?: string;
    lastDeliveryRange?: string;
    birthdayStart?: string;
    birthdayEnd?: string;
    remarkStart?: string;
    remarkEnd?: string;
    adviserGroup?: number;
    secondSaleOnly?: boolean;
};

export default () => {
    const {repo, hasPermission, userSession} = useUserSessionContext();
    const [loading, setLoading] = useState(false);
    const [datasource, setDatasource] = useState<PageList<SecondSaleYearCard>>();
    const [requestPage, setRequestPage] = useState<RequestPageInfo>({offset: 0});
    const [search, setSearch] = useState<SecondSaleSearch>({});
    const [adviserGroupOptions, setAdviserGroupOptions] = useState<{label: string; value: number}[]>([]);
    const [editCard, setEditCard] = useState<SecondSaleYearCard>();
    const [settingOpen, setSettingOpen] = useState(false);
    const canManageAll = hasPermission('4') === true;
    const categoryStorageKey = getCategoryStorageKey(userSession?.user.id);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
    const displayDatasource = datasource
        ? {...datasource, items: datasource.items.map(card => applyCategorySelection(card, selectedCategoryIds))}
        : datasource;

    const reload = (categoryIds = readStoredCategoryIds(categoryStorageKey), nextSearch: SecondSaleSearch = search) => {
        if (!repo) {
            return;
        }
        setLoading(true);
        repo.listSecondSaleYearCard({
            page: requestPage,
            ...nextSearch,
            categoryTagIds: categoryIds
        }).then(result => {
            const storedIds = readStoredCategoryIds(categoryStorageKey);
            if (storedIds === undefined) {
                const firstQueryCategoryIds = collectCategoryIds(result.items);
                if (firstQueryCategoryIds.length > 0) {
                    writeStoredCategoryIds(categoryStorageKey, firstQueryCategoryIds);
                    setSelectedCategoryIds(firstQueryCategoryIds);
                }
            }
            setDatasource(result);
        })
            .finally(() => setLoading(false));
    };

    useEffect(reload, [repo, requestPage, categoryStorageKey]);
    useEffect(() => {
        if (!repo) return;
        repo.listAfterSaleTags({type: ADVISER_GROUP_TAG})
            .then(v => setAdviserGroupOptions(v.map(s => ({label: s.name, value: s.id}))));
    }, [repo]);
    useEffect(() => {
        setSelectedCategoryIds(readStoredCategoryIds(categoryStorageKey) ?? []);
    }, [categoryStorageKey]);

    const executeSearch = (nextSearch: SecondSaleSearch = search) => {
        if ((requestPage.offset ?? 0) === 0) {
            reload(undefined, nextSearch);
        } else {
            setRequestPage(p => ({...p, offset: 0}));
        }
    };
    const changeYearCardTab = (value: string) => {
        const nextSearch: SecondSaleSearch = {
            ...search,
            adviserGroup: undefined,
            secondSaleOnly: value === SECOND_SALE_VALUE ? true : undefined
        };
        if (value !== ALL_GROUP_VALUE && value !== SECOND_SALE_VALUE) {
            nextSearch.adviserGroup = Number(value);
        }
        setSearch(nextSearch);
        executeSearch(nextSearch);
    };
    const clearSearch = () => {
        const nextSearch: SecondSaleSearch = {};
        setSearch(nextSearch);
        executeSearch(nextSearch);
    };
    const updateCardInDatasource = (card: SecondSaleYearCard) => {
        if (!datasource) {
            return;
        }
        const index = datasource.items.findIndex(v => v.id === card.id);
        if (index < 0) {
            return;
        }
        const items = [...datasource.items];
        items[index] = card;
        setDatasource({...datasource, items});
    };

    return <>
        <CommonTable
            onPageChange={(page, pageSize) => setRequestPage({...requestPage, offset: (page - 1) * pageSize, size: pageSize})}
            rightAction={<div style={filterPanelStyle}>
                <Segmented
                    value={getYearCardTabValue(search)}
                    options={[
                        {label: '全部年卡', value: ALL_GROUP_VALUE},
                        {label: '二销年卡', value: SECOND_SALE_VALUE},
                        ...adviserGroupOptions.map(option => ({
                            label: option.label.startsWith('二销') ? option.label : `二销${option.label}`,
                            value: option.value.toString()
                        }))
                    ]}
                    onChange={value => changeYearCardTab(value.toString())}/>
                <Space wrap size={8} style={filterRowStyle}>
                    <InputNumber placeholder='年卡ID'
                                 controls={false}
                                 precision={0}
                                 min={1}
                                 style={idSearchInputStyle}
                                 value={search.yearCardId}
                                 onChange={value => setSearch(p => ({
                                     ...p,
                                     yearCardId: typeof value === 'number' ? value : undefined
                                 }))}/>
                    <Input placeholder='年卡号'
                           allowClear
                           style={searchInputStyle}
                           value={search.yearCardOrderNo}
                           onChange={e => setSearch(p => ({...p, yearCardOrderNo: e.target.value || undefined}))}/>
                    <Input placeholder='客户手机号'
                           allowClear
                           style={searchInputStyle}
                           value={search.customerPhone}
                           onChange={e => setSearch(p => ({...p, customerPhone: e.target.value || undefined}))}/>
                    <Input placeholder='一销顾问手机号'
                           allowClear
                           style={adviserSearchInputStyle}
                           value={search.firstSaleAdviserPhone}
                           onChange={e => setSearch(p => ({...p, firstSaleAdviserPhone: e.target.value || undefined}))}/>
                    <Input placeholder='二销顾问手机号'
                           allowClear
                           style={adviserSearchInputStyle}
                           value={search.secondSaleAdviserPhone}
                           onChange={e => setSearch(p => ({...p, secondSaleAdviserPhone: e.target.value || undefined}))}/>
                    <Select placeholder='发货计划'
                            allowClear
                            style={selectInputStyle}
                            value={search.hasDeliveryPlan}
                            options={[
                                {label: '已设置', value: true},
                                {label: '未设置', value: false},
                            ]}
                            onChange={value => setSearch(p => ({...p, hasDeliveryPlan: value}))}/>
                    <Select placeholder='客户分层'
                            allowClear
                            style={selectInputStyle}
                            value={search.customerLevel}
                            options={CUSTOMER_LEVEL_OPTIONS}
                            onChange={value => setSearch(p => ({...p, customerLevel: value}))}/>
                    <Select placeholder='剩余盒数'
                            allowClear
                            style={remainingBoxSelectStyle}
                            value={search.remainingBoxRange}
                            options={REMAINING_BOX_RANGE_OPTIONS}
                            onChange={value => setSearch(p => ({...p, remainingBoxRange: value}))}/>
                    <Select placeholder='上次发货'
                            allowClear
                            style={lastDeliverySelectStyle}
                            value={search.lastDeliveryRange}
                            options={LAST_DELIVERY_RANGE_OPTIONS}
                            onChange={value => setSearch(p => ({...p, lastDeliveryRange: value}))}/>
                    <DatePicker.RangePicker
                        allowClear
                        placeholder={['生日开始', '生日结束']}
                        style={birthdayRangeInputStyle}
                        value={search.birthdayStart || search.birthdayEnd
                            ? [
                                search.birthdayStart ? dayjs(search.birthdayStart) : null,
                                search.birthdayEnd ? dayjs(search.birthdayEnd) : null
                            ]
                            : undefined}
                        onChange={(_, dateStrings) => setSearch(p => ({
                            ...p,
                            birthdayStart: dateStrings[0] || undefined,
                            birthdayEnd: dateStrings[1] || undefined
                        }))}/>
                    <DatePicker.RangePicker
                        allowClear
                        placeholder={['备注开始', '备注结束']}
                        style={birthdayRangeInputStyle}
                        value={search.remarkStart || search.remarkEnd
                            ? [
                                search.remarkStart ? dayjs(search.remarkStart) : null,
                                search.remarkEnd ? dayjs(search.remarkEnd) : null
                            ]
                            : undefined}
                        onChange={(_, dateStrings) => setSearch(p => ({
                            ...p,
                            remarkStart: dateStrings[0] || undefined,
                            remarkEnd: dateStrings[1] || undefined
                        }))}/>
                    <Button icon={<SearchOutlined/>}
                            onClick={() => executeSearch()}/>
                    <Button icon={<ClearOutlined/>}
                            onClick={clearSearch}>清空</Button>
                    <Button icon={<SettingOutlined/>}
                            onClick={() => setSettingOpen(true)}/>
                </Space>
            </div>}
            expandable={{
                expandedRowRender: card => <CategoryStats stats={card.categoryStats ?? []}/>
            }}
            loading={loading}
            {...displayDatasource}>
            <Table.Column
                width={YEAR_CARD_COLUMN_WIDTH}
                title={<Typography.Text ellipsis>年卡</Typography.Text>}
                render={(e: SecondSaleYearCard) => <div style={yearCardColumnStyle}>
                    <Cell
                        id={e.id}
                        name={e.yearCardName ?? '年卡'}
                        cover={e.yearCardCover}
                        idExtra={renderCardCreateTime(e.cardCreateTime)}
                        timeExtra={renderYearCardNoLine(e.yearCardNo)}/>
                </div>}/>
            <Table.Column
                width={CUSTOMER_COLUMN_WIDTH}
                title={<Typography.Text ellipsis>客户</Typography.Text>}
                render={(e: SecondSaleYearCard) => renderCustomer(e)}/>
            <Table.Column
                width={CARD_COUNT_COLUMN_WIDTH}
                title={renderColumnTitle('用户年卡数')}
                render={(e: SecondSaleYearCard) => renderYearCardCount(e)}/>
            <Table.Column
                width={ADVISER_COLUMN_WIDTH}
                title={<Typography.Text ellipsis>一销顾问</Typography.Text>}
                render={(e: SecondSaleYearCard) => <AdviserCell
                    repo={repo}
                    card={e}
                    type='first'
                    editable={canManageAll}
                    onComplete={updateCardInDatasource}/>}
            />
            <Table.Column
                width={ADVISER_COLUMN_WIDTH}
                title={<Typography.Text ellipsis>二销顾问</Typography.Text>}
                render={(e: SecondSaleYearCard) => <AdviserCell
                    repo={repo}
                    card={e}
                    type='second'
                    editable={canManageAll}
                    onComplete={updateCardInDatasource}/>}
            />
            <Table.Column
                align='right'
                width={BOX_COUNT_COLUMN_WIDTH}
                title={renderColumnTitle('月初库存')}
                render={(e: SecondSaleYearCard) => renderBoxCount(e.monthStartStockCount)}/>
            <Table.Column
                align='right'
                width={BOX_COUNT_COLUMN_WIDTH}
                title={renderColumnTitle('发货盒数')}
                render={(e: SecondSaleYearCard) => renderBoxCount(e.consumedBoxCount)}/>
            <Table.Column
                align='right'
                width={BOX_COUNT_COLUMN_WIDTH}
                title={renderColumnTitle('剩余盒数')}
                render={(e: SecondSaleYearCard) => renderBoxCount(e.remainingBoxCount)}/>
            <Table.Column
                width={DELIVERY_PLAN_COLUMN_WIDTH}
                title={renderColumnTitle('发货计划')}
                render={(e: SecondSaleYearCard) => renderBoolean(e.hasDeliveryPlan, deliveryPlanTagStyle)}/>
            <Table.Column
                width={LAST_DELIVERY_TIME_COLUMN_WIDTH}
                title={renderColumnTitle('上次发货时间')}
                render={(e: SecondSaleYearCard) => renderLastDeliveryTime(e.lastDeliveryTime)}/>
            <Table.Column
                width={EXPANDED_CONTENT_COLUMN_WIDTH}
                title={renderColumnTitle('扩品内容')}
                render={(e: SecondSaleYearCard) => renderTextContent(e.expandedProductContent, 150)}/>
            <Table.Column
                width={MAINTENANCE_COLUMN_WIDTH}
                title={renderColumnTitle('保养动作')}
                render={(e: SecondSaleYearCard) => renderTextContent(e.maintenanceActionContent, 130)}/>
            <Table.Column
                width={BIRTHDAY_COLUMN_WIDTH}
                title={renderColumnTitle('生日时间')}
                render={(e: SecondSaleYearCard) => renderBirthday(e.birthday)}/>
            <Table.Column
                width={BIRTHDAY_GIFT_COLUMN_WIDTH}
                title={renderColumnTitle('生日礼')}
                render={(e: SecondSaleYearCard) => renderTextContent(e.birthdayGiftContent, 130)}/>
            <Table.Column
                width={B_SAMPLE_COLUMN_WIDTH}
                title={renderColumnTitle('领取B品')}
                render={(e: SecondSaleYearCard) => renderBoolean(e.bSampleReceived, deliveryPlanTagStyle)}/>
            <Table.Column
                width={BOOLEAN_COLUMN_WIDTH}
                title={renderColumnTitle('是否报价')}
                render={(e: SecondSaleYearCard) => renderBoolean(e.quoted, deliveryPlanTagStyle)}/>
            <Table.Column
                width={REMARK_COLUMN_WIDTH}
                title={renderColumnTitle('备注')}
                render={(e: SecondSaleYearCard) => renderRemark(e)}/>
            <Table.Column
                align='right'
                title={<Typography.Text ellipsis>操作</Typography.Text>}
                render={(e: SecondSaleYearCard) => canEdit(e, canManageAll, userSession?.user.id)
                    ? <Button type='primary' size='small' onClick={() => setEditCard(e)}>编辑</Button>
                    : null}/>
        </CommonTable>
        {repo ? <SecondSaleEditModal
            repo={repo}
            card={editCard}
            onCancel={() => setEditCard(undefined)}
            onComplete={card => {
                setEditCard(undefined);
                updateCardInDatasource(card);
            }}/> : null}
        {repo ? <CategorySettingModal
            repo={repo}
            open={settingOpen}
            storageKey={categoryStorageKey}
            selectedCategoryIds={selectedCategoryIds}
            onCancel={() => setSettingOpen(false)}
            onComplete={ids => {
                setSelectedCategoryIds(ids);
                setSettingOpen(false);
                if ((requestPage.offset ?? 0) === 0) {
                    reload(ids);
                } else {
                    setRequestPage(p => ({...p, offset: 0}));
                }
            }}/> : null}
    </>;
};

const AdviserCell = (props: {
    repo?: Repository;
    card: SecondSaleYearCard;
    type: 'first' | 'second';
    editable: boolean;
    onComplete: (card: SecondSaleYearCard) => void;
}) => {
    const {repo, card, type, editable, onComplete} = props;
    const [editing, setEditing] = useState(false);
    const [phone, setPhone] = useState('');
    const [committing, setCommitting] = useState(false);
    const name = type === 'first' ? card.firstSaleAdviserName : card.adviserName;
    const currentPhone = type === 'first' ? card.firstSaleAdviserPhone : card.adviserPhone;
    const label = type === 'first' ? '一销顾问手机号' : '二销顾问手机号';

    useEffect(() => {
        if (!editing) {
            setPhone(currentPhone ?? '');
        }
    }, [currentPhone, editing]);

    const save = () => {
        if (!repo) return;
        const nextPhone = phone.trim();
        if (!nextPhone) {
            Modal.error({content: `请输入${label}`});
            return;
        }
        setCommitting(true);
        repo.updateSecondSaleYearCard(card.id, buildAdviserUpdatePayload(card, type, nextPhone))
            .then(updated => {
                setEditing(false);
                onComplete(updated);
            })
            .catch(e => Modal.error({content: formatErrorMessage(e)}))
            .finally(() => setCommitting(false));
    };

    return <div className='column' style={adviserColumnStyle}>
        <InfoLine contentPlaceHolder='-'>
            {name}
        </InfoLine>
        {editing ? <div style={adviserEditLineStyle}>
            <Input
                size='small'
                inputMode='tel'
                maxLength={20}
                placeholder='手机号'
                style={adviserEditInputStyle}
                value={phone}
                autoFocus
                onChange={e => setPhone(e.target.value)}
                onPressEnter={save}/>
            <Button
                type='text'
                size='small'
                title='保存'
                loading={committing}
                icon={<CheckOutlined/>}
                onClick={save}/>
            <Button
                type='text'
                size='small'
                title='取消'
                disabled={committing}
                icon={<CloseOutlined/>}
                onClick={() => {
                    setPhone(currentPhone ?? '');
                    setEditing(false);
                }}/>
        </div> : <div style={adviserPhoneLineStyle}>
            <Typography.Text title={currentPhone} ellipsis style={adviserPhoneTextStyle}>
                {currentPhone ?? '-'}
            </Typography.Text>
            {editable && repo ? <Button
                type='text'
                size='small'
                title='编辑顾问手机号'
                style={adviserEditButtonStyle}
                icon={<EditOutlined/>}
                onClick={() => {
                    setPhone(currentPhone ?? '');
                    setEditing(true);
                }}/> : null}
        </div>}
    </div>;
};

const CategoryStats = (props: {stats: SecondSaleCategoryStat[]}) => <div style={categoryStatsWrapperStyle}>
    <div style={{...categoryStatsRowStyle, ...categoryStatsHeaderStyle}}>
        <span>商品品类</span>
        <span style={categoryStatsNumberStyle}>月初库存</span>
        <span style={categoryStatsNumberStyle}>发货盒数</span>
        <span style={categoryStatsNumberStyle}>剩余盒数</span>
    </div>
    {(props.stats.length > 0 ? props.stats : []).map(stat => <div
        key={stat.categoryTagId}
        style={categoryStatsRowStyle}>
        <span>{stat.categoryName ?? '-'}</span>
        <span style={categoryStatsNumberStyle}>{stat.monthStartStockCount ?? 0}</span>
        <span style={categoryStatsNumberStyle}>{stat.deliveredCount ?? 0}</span>
        <span style={categoryStatsNumberStyle}>{stat.remainingCount ?? 0}</span>
    </div>)}
</div>;

const SecondSaleEditModal = (props: {
    repo: Repository;
    card?: SecondSaleYearCard;
    onCancel: () => void;
    onComplete: (card: SecondSaleYearCard) => void;
}) => {
    const {repo, card, onCancel, onComplete} = props;
    const [form] = Form.useForm();
    const [committing, setCommitting] = useState(false);
    useEffect(() => {
        if (card) {
            form.setFieldsValue({
                expandedProductContent: card.expandedProductContent,
                maintenanceActionContent: card.maintenanceActionContent,
                birthdayGiftContent: card.birthdayGiftContent,
                birthday: card.birthday ? dayjs(card.birthday) : undefined,
                bSampleReceived: card.bSampleReceived,
                quoted: card.quoted,
                remark: card.remark
            });
        }
    }, [card, form]);
    return <Modal
        title={`编辑二销年卡 ${card?.id ?? ''}`}
        open={!!card}
        destroyOnClose
        confirmLoading={committing}
        onCancel={onCancel}
        onOk={() => {
            form.validateFields().then(values => {
                if (!card) return;
                setCommitting(true);
                repo.updateSecondSaleYearCard(card.id, {
                    expandedProductContent: values.expandedProductContent,
                    maintenanceActionContent: values.maintenanceActionContent,
                    birthdayGiftContent: values.birthdayGiftContent,
                    birthday: values.birthday ? values.birthday.format('YYYY-MM-DD') : undefined,
                    bSampleReceived: values.bSampleReceived,
                    quoted: values.quoted,
                    remark: values.remark
                }).then(onComplete)
                    .catch(e => Modal.error({content: e}))
                    .finally(() => setCommitting(false));
            });
        }}>
        <Form form={form} layout='vertical'>
            <Form.Item label='扩品内容' name='expandedProductContent'>
                <Input.TextArea rows={3} maxLength={500} placeholder='请输入扩品内容'/>
            </Form.Item>
            <Form.Item label='保养动作' name='maintenanceActionContent'>
                <Input.TextArea rows={3} maxLength={500} placeholder='请输入保养动作'/>
            </Form.Item>
            <Form.Item label='生日日期' name='birthday'>
                <DatePicker style={{width: '100%'}}/>
            </Form.Item>
            <Form.Item label='生日礼' name='birthdayGiftContent'>
                <Input.TextArea rows={3} maxLength={500} placeholder='请输入生日礼'/>
            </Form.Item>
            <Form.Item label='是否领取B品试用装' name='bSampleReceived' valuePropName='checked'>
                <Switch/>
            </Form.Item>
            <Form.Item label='是否报价' name='quoted' valuePropName='checked'>
                <Switch/>
            </Form.Item>
            <Form.Item label='备注' name='remark'>
                <Input.TextArea rows={4} maxLength={1000}/>
            </Form.Item>
        </Form>
    </Modal>;
};

const CategorySettingModal = (props: {
    repo: Repository;
    open: boolean;
    storageKey: string;
    selectedCategoryIds: number[];
    onCancel: () => void;
    onComplete: (ids: number[]) => void;
}) => {
    const {repo, open, storageKey, selectedCategoryIds, onCancel, onComplete} = props;
    const [options, setOptions] = useState<{label: string; value: number}[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    useEffect(() => {
        if (!open) return;
        repo.listAfterSaleTags({type: PRODUCT_CATEGORY_TAG})
            .then(v => {
                const nextOptions = v.map(s => ({label: s.name, value: s.id}));
                const optionIds = new Set(nextOptions.map(s => s.value));
                const storedIds = readStoredCategoryIds(storageKey);
                const browserSelectedIds = storedIds ?? selectedCategoryIds;
                const nextSelectedIds = (browserSelectedIds.length > 0 ? browserSelectedIds : nextOptions.map(option => option.value))
                    .filter(id => optionIds.has(id));
                setOptions(nextOptions);
                setSelectedIds(nextSelectedIds);
            });
    }, [open, repo, storageKey, selectedCategoryIds]);
    return <Modal
        title='设置统计品类'
        open={open}
        destroyOnClose
        onCancel={onCancel}
        onOk={() => {
            if (selectedIds.length <= 0) {
                Modal.error({content: '请至少选择一个统计品类'});
                return;
            }
            writeStoredCategoryIds(storageKey, selectedIds);
            onComplete(selectedIds);
        }}>
        <Select
            mode='multiple'
            allowClear
            style={{width: '100%'}}
            placeholder='选择要统计的商品品类'
            value={selectedIds}
            options={options}
            onChange={setSelectedIds}/>
    </Modal>;
};

const getCategoryStorageKey = (userId?: string) => `${SECOND_SALE_CATEGORY_STORAGE_PREFIX}:${userId ?? 'anonymous'}`;

// 统计品类由当前浏览器 localStorage 控制；没有本地值时先查全部，再用接口返回的品类初始化本地值。
const readStoredCategoryIds = (storageKey: string): number[] | undefined => {
    if (typeof window === 'undefined') return undefined;
    try {
        const value = window.localStorage.getItem(storageKey);
        if (!value) return undefined;
        const ids = JSON.parse(value);
        return Array.isArray(ids) ? ids.filter(id => typeof id === 'number') : undefined;
    } catch {
        return undefined;
    }
};

const writeStoredCategoryIds = (storageKey: string, ids: number[]) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(storageKey, JSON.stringify(ids));
};

const collectCategoryIds = (cards: SecondSaleYearCard[]) => {
    const ids = new Set<number>();
    cards.forEach(card => (card.categoryStats ?? []).forEach(stat => ids.add(stat.categoryTagId)));
    return Array.from(ids);
};

const getYearCardTabValue = (search: SecondSaleSearch) => {
    if (search.secondSaleOnly) {
        return SECOND_SALE_VALUE;
    }
    return search.adviserGroup?.toString() ?? ALL_GROUP_VALUE;
};

const filterCategoryStats = (stats: SecondSaleCategoryStat[] = [], selectedCategoryIds: number[]) => {
    if (selectedCategoryIds.length <= 0) {
        return stats;
    }
    const idSet = new Set(selectedCategoryIds);
    return stats.filter(stat => idSet.has(stat.categoryTagId));
};

const applyCategorySelection = (card: SecondSaleYearCard, selectedCategoryIds: number[]): SecondSaleYearCard => {
    const categoryStats = filterCategoryStats(card.categoryStats ?? [], selectedCategoryIds);
    return {
        ...card,
        categoryStats,
        monthStartStockCount: categoryStats.reduce((sum, stat) => sum + (stat.monthStartStockCount ?? 0), 0),
        consumedBoxCount: categoryStats.reduce((sum, stat) => sum + (stat.deliveredCount ?? 0), 0),
        remainingBoxCount: categoryStats.reduce((sum, stat) => sum + (stat.remainingCount ?? 0), 0)
    };
};

const canEdit = (card: SecondSaleYearCard, canManageAll: boolean, userId?: string) => {
    return canManageAll || (!!userId && card.adviserUserId === userId);
};

const buildAdviserUpdatePayload = (
    card: SecondSaleYearCard,
    type: 'first' | 'second',
    phone: string
): Partial<SecondSaleYearCard> => ({
    firstSaleAdviserPhone: type === 'first' ? phone : card.firstSaleAdviserPhone,
    adviserPhone: type === 'second' ? phone : card.adviserPhone,
    expandedProductContent: card.expandedProductContent,
    maintenanceActionContent: card.maintenanceActionContent,
    birthdayGiftContent: card.birthdayGiftContent,
    birthday: card.birthday,
    bSampleReceived: card.bSampleReceived,
    quoted: card.quoted,
    remark: card.remark
});

const formatErrorMessage = (e: any) => {
    if (!e) return '保存失败';
    if (typeof e === 'string') return e;
    return e.message ?? JSON.stringify(e);
};

const renderBoolean = (value?: boolean, style?: CSSProperties) => value
    ? <Tag color='green' style={style}>是</Tag>
    : <Tag style={style}>否</Tag>;

const renderBoxCount = (value?: number) => <Typography.Text style={boxCountTextStyle}>
    {value ?? 0}
</Typography.Text>;

const renderLastDeliveryTime = (value?: string) => value
    ? dayjs(value).format('YYYY-MM-DD HH:mm')
    : <Typography.Text type='secondary'>未发货</Typography.Text>;

const renderTextContent = (value?: string, maxWidth = 90) => value
    ? <Tooltip title={value}>
        <span style={{...multiLineTextStyle, maxWidth}}>
            {value}
        </span>
    </Tooltip>
    : <Typography.Text type='secondary'>-</Typography.Text>;

const renderRemark = (card: SecondSaleYearCard) => card.remark
    ? <Tooltip title={`备注时间：${formatRemarkTime(card.remarkTime)}\n${card.remark}`}>
        <div style={remarkWrapperStyle}>
            <Typography.Text type='secondary' style={remarkTimeStyle}>
                备注时间：{formatRemarkTime(card.remarkTime)}
            </Typography.Text>
            <span style={remarkContentStyle}>
                {card.remark}
            </span>
        </div>
    </Tooltip>
    : <Typography.Text type='secondary'>-</Typography.Text>;

const renderBirthday = (value?: string) => <Typography.Text style={birthdayTextStyle}>
    {value || '-'}
</Typography.Text>;

const renderYearCardCount = (card: SecondSaleYearCard) => <div style={yearCardCountStyle}>
    <Typography.Text style={{...yearCardCountTextStyle, color: '#389e0d'}}>
        正常年卡数：{card.normalYearCardCount ?? 0}
    </Typography.Text>
    <Typography.Text style={{...yearCardCountTextStyle, color: '#d48806'}}>
        零库存年卡：{card.zeroStockYearCardCount ?? 0}
    </Typography.Text>
    <Typography.Text style={{...yearCardCountTextStyle, color: '#cf1322'}}>
        删除年卡数：{card.deletedYearCardCount ?? 0}
    </Typography.Text>
</div>;

const renderColumnTitle = (title: string) => <Typography.Text style={{whiteSpace: 'nowrap'}}>{title}</Typography.Text>;

const renderCardCreateTime = (value?: string) => <Label>
    {formatShortCreateTime(value)} 创建
</Label>;

const formatShortCreateTime = (value?: string) => {
    if (!value) return '-';
    return dayjs(value).format('YYYY/MM/DD HH:mm');
};

const renderYearCardNo = (yearCardNo?: string, style?: CSSProperties) => {
    if (!yearCardNo) return '-';
    return <Typography.Text title={yearCardNo} style={{...style, ...yearCardNoTextStyle}}>
        {yearCardNo}
    </Typography.Text>;
};

const renderYearCardNoLine = (yearCardNo?: string) => <div style={yearCardNoLineStyle}>
    {yearCardNo ? <Button
        type='text'
        size='small'
        style={{width: 16, height: 16, padding: 0, flex: '0 0 16px'}}
        icon={<CopyOutlined/>}
        onClick={() => copyYearCardNo(yearCardNo)}/> : null}
    {renderYearCardNo(yearCardNo, smallTextStyle)}
</div>;

const copyYearCardNo = (yearCardNo?: string) => {
    if (!yearCardNo) return;
    navigator.clipboard?.writeText(yearCardNo);
};

const formatMoney = (money?: number) => typeof money === 'number' ? `¥${(money / 100).toFixed(0)}` : '-';

const formatRemarkTime = (value?: string) => value ? dayjs(value).format('YYYY-MM-DD') : '-';

const smallTextStyle: CSSProperties = {fontSize: 12};
const priceTextStyle: CSSProperties = {fontSize: 14};
const adviserPhoneLineStyle: CSSProperties = {display: 'flex', alignItems: 'center', gap: 2, width: '100%', minHeight: 24};
const adviserPhoneTextStyle: CSSProperties = {maxWidth: 116, lineHeight: '22px'};
const adviserEditLineStyle: CSSProperties = {display: 'flex', alignItems: 'center', gap: 2, width: '100%'};
const adviserEditInputStyle: CSSProperties = {width: 116};
const adviserEditButtonStyle: CSSProperties = {width: 20, height: 20, padding: 0, flex: '0 0 20px'};
const categoryStatsWrapperStyle: CSSProperties = {
    width: 456,
    marginLeft: 42,
    border: '1px solid #f0f0f0',
    borderRadius: 4,
    overflow: 'hidden',
    background: '#fff'
};
const categoryStatsRowStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 84px 84px 84px',
    gap: 12,
    alignItems: 'center',
    minHeight: 30,
    padding: '4px 12px',
    borderBottom: '1px solid #f5f5f5',
    fontSize: 12
};
const categoryStatsHeaderStyle: CSSProperties = {fontWeight: 600, background: '#fafafa'};
const categoryStatsNumberStyle: CSSProperties = {textAlign: 'right'};
const yearCardNoLineStyle: CSSProperties = {display: 'flex', alignItems: 'flex-start', gap: 4, width: '100%'};
const yearCardNoTextStyle: CSSProperties = {
    display: 'inline-block',
    flex: 1,
    minWidth: 0,
    whiteSpace: 'normal',
    wordBreak: 'break-all',
    lineHeight: '18px'
};
const birthdayTextStyle: CSSProperties = {fontSize: 12, whiteSpace: 'nowrap'};
const yearCardCountStyle: CSSProperties = {width: 130, display: 'flex', flexDirection: 'column', gap: 2};
const yearCardCountTextStyle: CSSProperties = {fontSize: 14, fontWeight: 600, lineHeight: '22px', whiteSpace: 'nowrap'};
const boxCountTextStyle: CSSProperties = {fontSize: 20, fontWeight: 700};
const deliveryPlanTagStyle: CSSProperties = {fontSize: 18, lineHeight: '24px', padding: '0 8px'};
const multiLineTextStyle: CSSProperties = {
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 4,
    whiteSpace: 'normal',
    wordBreak: 'break-all',
    lineHeight: '18px',
    maxHeight: 72
};
const remarkWrapperStyle: CSSProperties = {width: 170, display: 'flex', flexDirection: 'column', gap: 2};
const remarkTimeStyle: CSSProperties = {fontSize: 12, lineHeight: '18px', whiteSpace: 'nowrap'};
const remarkContentStyle: CSSProperties = {
    ...multiLineTextStyle,
    maxWidth: 170,
    WebkitLineClamp: 3,
    maxHeight: 54
};

const renderCustomer = (card: SecondSaleYearCard) => {
    const level = getCustomerLevel(card.money);
    const textStyle = getCustomerLevelTextStyle(level);
    return <div style={personColumnStyle}>
        <div style={{lineHeight: '20px'}}>
            <Typography.Text style={{...textStyle, ...priceTextStyle}}>
                {formatMoney(card.money)}
            </Typography.Text>
        </div>
        <Tooltip title={CUSTOMER_LEVEL_RULE}>
            <div style={{lineHeight: '22px', cursor: 'help'}}>
                <Typography.Text style={{...textStyle, cursor: 'help'}}>
                    用户层级：{level}
                </Typography.Text>
            </div>
        </Tooltip>
        <div style={{lineHeight: '22px'}}>
            <Typography.Text ellipsis style={card.customerName ? {...textStyle, maxWidth: 98} : {maxWidth: 98}}>
                {card.customerName ?? '-'}
            </Typography.Text>
        </div>
        <div style={{lineHeight: '22px'}}>
            <Typography.Text style={card.customerPhone ? textStyle : undefined}>
                {card.customerPhone ?? '-'}
            </Typography.Text>
        </div>
    </div>;
};

const getCustomerLevel = (money?: number) => {
    const value = money ?? 0;
    if (value >= 700000) return 'A';
    if (value >= 500000) return 'B';
    if (value >= 300000) return 'C';
    return 'D';
};

const getCustomerLevelColor = (money?: number) => {
    switch (getCustomerLevel(money)) {
        case 'A': return 'gold';
        case 'B': return 'red';
        case 'C': return 'blue';
        default: return 'green';
    }
};

const getCustomerLevelTextStyle = (level: string): { color?: string; fontWeight?: number } => {
    switch (level) {
        case 'A':
            return {color: '#d48806', fontWeight: 600};
        case 'B':
            return {color: '#cf1322'};
        case 'C':
            return {color: '#1677ff'};
        default:
            return {color: '#389e0d'};
    }
};
